import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  ArrowRight,
  Shield,
  CheckCircle,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useToast, ToastContainer } from "../components/Toast";
import styles from "./Login.module.css";

const Login = () => {
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  const { toasts, success, error } = useToast();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    acceptTerms: false,
    acceptPrivacy: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          success("Login successful! Welcome back.");
          navigate("/dashboard");
        } else {
          error(result.error || "Login failed. Please try again.");
        }
      } else {
        // Validate passwords match
        if (formData.password !== formData.confirmPassword) {
          error("Passwords do not match.");
          setIsLoading(false);
          return;
        }

        // Validate terms acceptance
        if (!formData.acceptTerms) {
          error("Please accept the Terms & Conditions to continue.");
          setIsLoading(false);
          return;
        }

        if (!formData.acceptPrivacy) {
          error("Please accept the Privacy Policy to continue.");
          setIsLoading(false);
          return;
        }

        const result = await signup({
          ...formData,
          termsAcceptedAt: new Date().toISOString(),
          privacyAcceptedAt: new Date().toISOString(),
        });
        if (result.success) {
          success("Account created successfully! Welcome to ProSkillNest.");
          navigate("/dashboard");
        } else {
          error(result.error || "Signup failed. Please try again.");
        }
      }
    } catch (err) {
      error("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/forgot-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: forgotEmail }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        success(data.message);
        // In development, if token is returned, show reset form
        if (data.resetToken) {
          setResetToken(data.resetToken);
          setShowForgotPassword(false);
          setShowResetPassword(true);
        } else {
          setShowForgotPassword(false);
        }
      } else {
        error(data.error || "Failed to send reset email.");
      }
    } catch (err) {
      error("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      error("Passwords do not match.");
      return;
    }

    if (newPassword.length < 6) {
      error("Password must be at least 6 characters long.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/reset-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: resetToken, newPassword }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        success("Password reset successfully! You can now login.");
        setShowResetPassword(false);
        setResetToken("");
        setNewPassword("");
        setConfirmNewPassword("");
      } else {
        error(data.error || "Failed to reset password.");
      }
    } catch (err) {
      error("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className={styles.loginPage}>
      <div className={styles.authContainer}>
        <div className={styles.authContent}>
          <div className={styles.authVisual}>
            <div className={styles.visualContent}>
              <h2>Master Your Skills with ProSkillNest</h2>
              <div className={styles.featuresList}>
                <div className={styles.featureItem}>
                  <CheckCircle size={20} />
                  <span>Personalized skill assessments</span>
                </div>
                <div className={styles.featureItem}>
                  <CheckCircle size={20} />
                  <span>AI-powered learning paths</span>
                </div>
                <br />
                <div className={styles.featureItem}>
                  <CheckCircle size={20} />
                  <span>Real-time progress tracking</span>
                </div>
              </div>
              <div className={styles.trustIndicators}>
                <div className={styles.trustItem}>
                  <Shield size={16} />
                  <span>Secure & Private</span>
                </div>
                <div className={styles.trustItem}>
                  <span>10+ Active Learners</span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.authFormContainer}>
            <div className={styles.authForm}>
              <div className={styles.formHeader}>
                <h1 className={styles.authTitle}>
                  {isLogin ? "Welcome Back" : "Create Account"}
                </h1>
                <p className={styles.authSubtitle}>
                  {isLogin
                    ? "Sign in to continue your learning journey"
                    : "Join thousands of learners advancing their careers"}
                </p>
              </div>

              <form onSubmit={handleSubmit} className={styles.authFormContent}>
                {!isLogin && (
                  <>
                    <div className={styles.formGroup}>
                      <label htmlFor="firstName">First Name</label>
                      <div className={styles.inputWrapper}>
                        <User size={18} className={styles.inputIcon} />
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          placeholder="Enter your first name"
                          value={formData.firstName}
                          onChange={handleChange}
                          required={!isLogin}
                        />
                      </div>
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="lastName">Last Name</label>
                      <div className={styles.inputWrapper}>
                        <User size={18} className={styles.inputIcon} />
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          placeholder="Enter your last name"
                          value={formData.lastName}
                          onChange={handleChange}
                          required={!isLogin}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className={styles.formGroup}>
                  <label htmlFor="email">Email Address</label>
                  <div className={styles.inputWrapper}>
                    <Mail size={18} className={styles.inputIcon} />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="password">Password</label>
                  <div className={styles.inputWrapper}>
                    <Lock size={18} className={styles.inputIcon} />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {!isLogin && (
                  <div className={styles.formGroup}>
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <div className={styles.inputWrapper}>
                      <Lock size={18} className={styles.inputIcon} />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        placeholder="Confirm your password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required={!isLogin}
                      />
                      <button
                        type="button"
                        className={styles.passwordToggle}
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {!isLogin && (
                  <div className={styles.termsAcceptance}>
                    <div className={styles.checkboxGroup}>
                      <label className={`${styles.checkboxWrapper} ${styles.required}`}>
                        <input
                          type="checkbox"
                          name="acceptTerms"
                          checked={formData.acceptTerms}
                          onChange={handleChange}
                          required={!isLogin}
                        />
                        <span className={styles.checkmark}></span>
                        <span className={styles.checkboxText}>
                          I agree to the{" "}
                          <a
                            href="/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.legalLink}
                          >
                            Terms & Conditions
                          </a>
                          <span className={styles.requiredAsterisk}>*</span>
                        </span>
                      </label>
                    </div>

                    <div className={styles.checkboxGroup}>
                      <label className={`${styles.checkboxWrapper} ${styles.required}`}>
                        <input
                          type="checkbox"
                          name="acceptPrivacy"
                          checked={formData.acceptPrivacy}
                          onChange={handleChange}
                          required={!isLogin}
                        />
                        <span className={styles.checkmark}></span>
                        <span className={styles.checkboxText}>
                          I agree to the{" "}
                          <a
                            href="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.legalLink}
                          >
                            Privacy Policy
                          </a>
                          <span className={styles.requiredAsterisk}>*</span>
                        </span>
                      </label>
                    </div>

                    <div className={styles.legalNotice}>
                      <Shield size={16} />
                      <span>
                        Your data is protected and will be used in accordance
                        with our privacy policy.
                      </span>
                    </div>
                  </div>
                )}

                {isLogin && (
                  <div className={styles.formOptions}>
                    {/* <label className={styles.checkboxWrapper}>
                      <input type="checkbox" />
                      <span className={styles.checkmark}></span>
                      Remember me
                    </label> */}
                    <button
                      type="button"
                      className={styles.forgotPassword}
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary auth-btn"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    "Processing..."
                  ) : (
                    <>
                      {isLogin ? "Sign In" : "Create Account"}
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>

              <div className={styles.authSwitch}>
                <p>
                  {isLogin
                    ? "Don't have an account? "
                    : "Already have an account? "}
                  <button
                    type="button"
                    className={styles.switchBtn}
                    onClick={() => setIsLogin(!isLogin)}
                  >
                    {isLogin ? "Sign Up" : "Sign In"}
                  </button>
                </p>
              </div>

              {/* <div className={styles.authDivider}>
                <span>or continue with</span>
              </div>

              <div className={styles.socialAuth}>
                <button type="button" className={`${styles.socialBtn} ${styles.google}`}>
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Google
                </button>
                <button type="button" className={`${styles.socialBtn} ${styles.github}`}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                  GitHub
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>
      <ToastContainer toasts={toasts} position="top-right" />

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowForgotPassword(false)}
        >
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Reset Password</h2>
              <button
                className={styles.modalClose}
                onClick={() => setShowForgotPassword(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleForgotPassword}>
              <p className={styles.modalDescription}>
                Enter your email address and we'll send you instructions to
                reset your password.
              </p>
              <div className={styles.formGroup}>
                <label htmlFor="forgotEmail">Email Address</label>
                <div className={styles.inputWrapper}>
                  <Mail size={18} className={styles.inputIcon} />
                  <input
                    type="email"
                    id="forgotEmail"
                    placeholder="Enter your email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : "Send Reset Link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowResetPassword(false)}
        >
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Set New Password</h2>
              <button
                className={styles.modalClose}
                onClick={() => setShowResetPassword(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleResetPassword}>
              <p className={styles.modalDescription}>
                Enter your new password below.
              </p>
              <div className={styles.formGroup}>
                <label htmlFor="newPassword">New Password</label>
                <div className={styles.inputWrapper}>
                  <Lock size={18} className={styles.inputIcon} />
                  <input
                    type="password"
                    id="newPassword"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="confirmNewPassword">Confirm New Password</label>
                <div className={styles.inputWrapper}>
                  <Lock size={18} className={styles.inputIcon} />
                  <input
                    type="password"
                    id="confirmNewPassword"
                    placeholder="Confirm new password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
              </div>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowResetPassword(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default Login;
